/**
 * Event Tracking Hook
 */

import { useCallback } from 'react';
import { useEngageTrack } from './useEngageTrack';

/**
 * Hook to create a memoized tracking function for specific events
 * Useful for button clicks, form submissions, etc.
 * 
 * @param eventType - Type of event to track
 * @param baseData - Base data to include with every event (optional)
 * @returns Tracking function that accepts additional data
 * 
 * @example
 * ```tsx
 * function SignUpButton() {
 *   const trackSignUp = useTrackEvent('signup_click', { button: 'header' });
 *   
 *   return (
 *     <button onClick={() => trackSignUp({ timestamp: Date.now() })}>
 *       Sign Up
 *     </button>
 *   );
 * }
 * ```
 */
export const useTrackEvent = (
  eventType: string,
  baseData?: Record<string, unknown>
): ((additionalData?: Record<string, unknown>) => void) => {
  const { track } = useEngageTrack();

  return useCallback(
    (additionalData?: Record<string, unknown>) => {
      track(eventType, {
        ...baseData,
        ...additionalData,
      });
    },
    [track, eventType, baseData]
  );
};
